import { isoDateTime, z } from "../zod.js";
import { Uuid } from "../identity/admin-schemas.js";

// The shared-kernel FILE REGISTRY contract (D-064). Not a numbered module:
// spec 07 §0 lists `files` among "shared-kernel platform services", and three
// P2 modules (04 production, 05 waste, 07 quality) plus two generated-document
// flows depend on it. Built once in P2-0 so the evidence columns those modules
// add are `uuid[] REFERENCES files(id)` rather than three different spellings
// of a raw S3 key (spec 04 §2.11 / 05 §2.2 modelled `text[]` of object keys —
// a shape that CANNOT be tenant-fenced on a presigned GET).
//
// THE UPLOAD IS THREE STEPS, and each is idempotent and retriable — which is
// what makes it usable from the floor PWA's offline outbox (a phone in a cable
// hall loses the network mid-upload as a matter of routine):
//   1. POST /v1/files            → registry row (`pending`) + a presigned PUT
//   2. PUT <upload_url>          → the bytes, straight to S3, no API hop
//   3. POST /v1/files/{id}/store → the server READS THE OBJECT BACK, records
//                                  the authoritative byte_size + sha256, and
//                                  moves the row to `stored`
// Step 3 is not ceremony. Until it runs, nothing in the system knows whether
// the object exists at all, and `sha256` is only what the client asserted. A
// file is resolvable for download ONLY in `stored` — so a half-uploaded or
// tampered object can never be served as evidence.

// The evidence-photo limits (D-096) live in ./limits.js — a zero-import module
// with its own package export so the floor PWA can consume them without the
// OpenAPI registry — and are re-exported here for the server side.
export { FILE_PHOTO_CONTENT_TYPES, FILE_PHOTO_MAX_BYTES } from "./limits.js";

/** The registered purposes (migration 0017 CHECK constraint — same list). */
export const FILE_PURPOSES = [
  "production_evidence",
  "waste_evidence",
  "quality_evidence",
  "quality_coa_pdf",
  "quality_coa_logo",
  "production_traveler_pdf",
  // Module 09 §5 send (slice P3-C-1): the rendered quote offer the customer is
  // sent a link to. Migration 0055 widened `files_purpose_check` to admit it,
  // and this list, FILE_PURPOSE_SPECS and that CHECK are held equal in all
  // three directions by files-registry.int.test.ts — so the purpose lands with
  // the migration that admits it, not with the renderer that will write it.
  "sales_quote_pdf",
  // Module 09 §3.7 step 4 (slice P3-D PR-D4, D-288 ruling 10): the delivery
  // note, rendered ONCE at pack and frozen, so the paper that travels with the
  // goods is the paper the storekeeper printed. Admitted by migration 0064.
  // The proforma has NO purpose: it is rendered on demand and stored nowhere.
  "sales_delivery_note_pdf",
  // Module 17 §2.8 (P3-E2-2 R-0, D-322 ruling 1): every stored media item of
  // a thread, both the inbound fetch and the office reply's attachment. Before
  // migration 0076 these lived as raw keys under `tenants/`, a prefix the
  // imports bucket expires.
  "messaging_media",
  // Module 02 §2.8 (D-322 ruling 4): the rendered QR print kit, served by the
  // kit door after its own check. Admitted by migration 0076 with the above.
  "onboarding_qr_kit_pdf",
  // Module 09 §2.29 (P3-E2 PR E2-2a2, D-323 rulings 8 to 13, D-335): a file
  // a person attaches to a customer, a quote or an order, uploaded from the
  // browser and served by module 09's attachment door after its own check.
  // Admitted by migration 0080.
  "sales_attachment",
  // Module 17 §2.8 (P3-E2 PR E2-2e, D-327 ruling 14): a part of an inbound
  // mail, stored by the email worker after it has checked the bytes, and
  // served by module 17's media door after its own check. Admitted by
  // migration 0081.
  "messaging_email_attachment",
] as const;

export const FilePurpose = z.enum(FILE_PURPOSES).openapi("FilePurpose");

/**
 * The office document types (D-322 ruling 2, D-323 ruling 9). Defined with
 * the registry because two purposes read it: `messaging_media` admits every
 * one of them, and module 09's attachment purpose (E2-2a2) and module 17's
 * email intake (E2-2e) import this one constant. PDF, PNG and JPEG are
 * sniffed from their bytes; XLSX, DOCX and PPTX are recognised by the OOXML
 * main part (apps/api/src/platform/files/content-sniff.ts). CSV and TXT carry
 * no signature. A purpose without `verifyBytes` keeps either as declared
 * without reading it, and a purpose with it (`sales_attachment`) holds the
 * bytes to the strict text check in the same file (`isPlainText`, D-323
 * ruling 11), so a declared CSV must decode as text and hold no control
 * character but tab, line feed and carriage return.
 */
export const OFFICE_DOCUMENT_CONTENT_TYPES = [
  "application/pdf",
  "image/png",
  "image/jpeg",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "text/csv",
  "text/plain",
] as const;

/**
 * What a `messaging_media` file may be stored as (D-322 ruling 2). It is the
 * composer's upload allowlist (`MEDIA_MIME_ALLOWLIST` in module 17, a subset
 * held by apps/api/test/unit/content-sniff.test.ts), the office document
 * types above, `video/mp4` for an inbound Telegram video, and
 * `application/octet-stream` for an inbound item whose bytes match nothing
 * the registry can name. An octet-stream item is served as an attachment and
 * never rendered inline.
 */
export const MESSAGING_MEDIA_CONTENT_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "text/csv",
  "text/plain",
  "audio/mpeg",
  "audio/ogg",
  "audio/mp4",
  "audio/wav",
  "video/mp4",
  "application/octet-stream",
] as const;

/**
 * What a `sales_attachment` file may be (spec 09 §2.29, D-323 ruling 9,
 * D-335 ruling 2): the office document types above and an AutoCAD drawing,
 * stored as `application/acad`. A cable factory's customers send drawings,
 * and a DWG is checked by its version header. The drawing joins this purpose
 * alone, so `OFFICE_DOCUMENT_CONTENT_TYPES` and the messaging list are
 * unchanged. Every archive, every macro-capable and legacy Office format, SVG
 * and HTML stay out, because nothing verifies what an archive holds and the
 * rest are documents a browser or an Office client executes. It lives in the
 * contract so the upload dialog and the registry read one list. `POST /v1/files` also
 * takes `image/vnd.dwg` as a declared alias of the drawing and answers with
 * the row's `application/acad`, the type the browser then sends its PUT
 * under (`SALES_ATTACHMENT_CONTENT_TYPE_ALIASES`).
 */
export const SALES_ATTACHMENT_CONTENT_TYPES = [
  ...OFFICE_DOCUMENT_CONTENT_TYPES,
  "application/acad",
] as const;

/** Declared types `POST /v1/files` rewrites for `sales_attachment` before it
 * presigns (D-335 ruling 2): IANA's `image/vnd.dwg` is the drawing a browser
 * on some systems names, and the registry stores it as `application/acad`. */
export const SALES_ATTACHMENT_CONTENT_TYPE_ALIASES: Readonly<Record<string, string>> = {
  "image/vnd.dwg": "application/acad",
};

/** The `sales_attachment` byte cap (spec 09 §2.29): 25 MB, the size a drawing
 * set or a scanned contract reaches and the messaging media cap besides. */
export const SALES_ATTACHMENT_MAX_BYTES = 25 * 1024 * 1024;

/**
 * What a `messaging_email_attachment` file may be (spec 17 §2.8, D-327
 * ruling 14): the office document types above, `image/webp` and `image/gif`,
 * which §2.8 stores as photos with JPEG and PNG, and the AutoCAD drawing
 * `sales_attachment` admits, because customers mail drawings as often as they
 * attach them to an order. Only the email worker writes the purpose, through
 * `FilesService.prepareGenerated`, and it decides each part's type before
 * that call. It normalises the declaration through
 * `SALES_ATTACHMENT_CONTENT_TYPE_ALIASES` and checks the bytes against it, so
 * the registry adds no byte check of its own for this purpose.
 */
export const MESSAGING_EMAIL_ATTACHMENT_CONTENT_TYPES = [
  ...OFFICE_DOCUMENT_CONTENT_TYPES,
  "image/webp",
  "image/gif",
  "application/acad",
] as const;

/** The `messaging_email_attachment` byte cap (spec 17 §2.8): 25 MB, the cap
 * the `message_media.byte_size` CHECK holds for every stored item of a
 * thread. */
export const MESSAGING_EMAIL_ATTACHMENT_MAX_BYTES = 25 * 1024 * 1024;

export type FilePurposeName = (typeof FILE_PURPOSES)[number];

export const FileStatus = z.enum(["pending", "stored", "rejected"]).openapi("FileStatus");

/** Lowercase hex SHA-256 of the bytes the client is about to upload. */
export const Sha256Hex = z
  .string()
  .regex(/^[0-9a-f]{64}$/u, "sha256 must be 64 lowercase hex characters")
  .openapi({ example: "e3b0c44298fc1c149afbf4c8996fb92427ae41e4649b934ca495991b7852b855" });

export const FileRecord = z
  .object({
    id: Uuid,
    purpose: FilePurpose,
    status: FileStatus,
    content_type: z.string(),
    /** Authoritative once `status === 'stored'`; client-declared before that. */
    byte_size: z.number().int().nonnegative(),
    /** Authoritative once `status === 'stored'`; client-declared before that. */
    sha256: Sha256Hex,
    file_name: z.string().nullable(),
    facility_id: Uuid.nullable(),
    uploaded_by_user_id: Uuid,
    created_at: isoDateTime(),
    stored_at: isoDateTime().nullable(),
    /** Set iff `status === 'rejected'` — why the object failed verification. */
    rejected_reason: z.string().nullable(),
  })
  .openapi("FileRecord");

export const FileCreateRequest = z
  .object({
    purpose: FilePurpose,
    content_type: z.string().min(1).max(255),
    /** Declared size, checked against the purpose's cap BEFORE presigning. */
    byte_size: z.number().int().positive(),
    /** Declared digest, VERIFIED against the stored object at step 3. */
    sha256: Sha256Hex,
    file_name: z.string().min(1).max(255).optional(),
    /** Capture facility (descriptor, D-043/D-069). Omit for tenant-level docs. */
    facility_id: Uuid.optional(),
  })
  .openapi("FileCreateRequest");

export const FileCreateResponse = z
  .object({
    file: FileRecord,
    /** Presigned S3 PUT. Short-lived; re-createable by replaying the request. */
    upload_url: z.url(),
    upload_expires_at: isoDateTime(),
  })
  .openapi("FileCreateResponse");

export const FileStoreResponse = z.object({ file: FileRecord }).openapi("FileStoreResponse");

export const FileDownloadResponse = z
  .object({
    file: FileRecord,
    /** Short-lived presigned GET, minted from the ROW's key — never from a
     * caller-supplied key. Only a member of the owning tenant reaches it. */
    download_url: z.url(),
    download_expires_at: isoDateTime(),
  })
  .openapi("FileDownloadResponse");

/**
 * RFC 9457 problem codes this surface returns (D-022 UPPER_SNAKE machine keys).
 *
 * `NOT_FOUND` is deliberately shared with every other unknown-id answer in the
 * product: a cross-tenant file id and a nonexistent file id must be
 * indistinguishable, or the 404/403 split becomes an id oracle (the module-06
 * rule — spec 06 §9.10).
 */
export const FILE_PROBLEM_CODES = [
  "FILE_PURPOSE_UNKNOWN",
  "FILE_PURPOSE_NOT_UPLOADABLE",
  "FILE_CONTENT_TYPE_NOT_ALLOWED",
  "FILE_TOO_LARGE",
  "FILE_NOT_UPLOADED",
  "FILE_INTEGRITY_MISMATCH",
  "FILE_NOT_STORED",
  "FILE_NOT_DIRECTLY_READABLE",
] as const;

export type FileProblemCode = (typeof FILE_PROBLEM_CODES)[number];
